<script setup lang="ts">
/**
 * 内容区"欢迎门"——未登录/未开通时显示，引导登录或免费开通。
 * 不全屏拦截:侧栏菜单可见(能逛),内容区给清晰引导。
 *
 * 主按钮 = 登录(已被客服开通好的店主回访用)。
 * 次按钮 = 免费开通试用 → 直接打开自助开通表单(LoginModal 的 free 模式:
 *   填 appid/AppSecret/档口名/手机号)。自己搞不定的,再走「联系客服」兜底。
 */
import { ref } from 'vue'
import { useAuthModal } from '@/composables/useAuthModal'
import SupportQrCard from './SupportQrCard.vue'

const { openAuth } = useAuthModal()
const showContact = ref(false)
</script>

<template>
  <div class="welcome">
    <div class="welcome-card">
      <span class="welcome-badge"><i class="ti ti-photo-search" aria-hidden="true"></i></span>
      <h2 class="welcome-title">欢迎使用面料档口管理台</h2>
      <p class="welcome-sub">登录你的账号，或免费开通试用，开始整理图库、管理商品。</p>
      <div class="welcome-actions">
        <button class="wbtn wbtn-primary" type="button" @click="openAuth('login')">
          <i class="ti ti-login-2" aria-hidden="true"></i> 登录
        </button>
        <button class="wbtn" type="button" @click="openAuth('free')">
          <i class="ti ti-gift" aria-hidden="true"></i> 免费开通试用
        </button>
      </div>
      <p class="welcome-hint">
        免费档：200 张额度 · 小程序展示。搜图 / 进销存付费开通。
        <br />搞不定？<button class="link" type="button" @click="showContact = true">联系客服</button>帮你开通。
      </p>
    </div>

    <!-- 免费开通试用 = 联系客服指引(免费档需绑定小程序，客服带开通) -->
    <div v-if="showContact" class="modal-mask" @click.self="showContact = false">
      <div class="contact-card">
        <button class="modal-close" type="button" @click="showContact = false">
          <i class="ti ti-x" aria-hidden="true"></i>
        </button>
        <span class="contact-badge"><i class="ti ti-brand-wechat" aria-hidden="true"></i></span>
        <h3 class="contact-title">联系客服，免费帮你开通</h3>
        <p class="contact-sub">
          免费档需要绑定你的微信小程序（AppID / AppSecret）。加客服微信，我们一步步带你开通配置，省心。
        </p>
        <SupportQrCard class="wg-qr" :size="160" />
        <p class="contact-foot">已经开通过？直接<button class="link" type="button" @click="showContact = false; openAuth('login')">登录</button></p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.welcome {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.welcome-card {
  text-align: center;
  max-width: 420px;
}
.welcome-badge {
  width: 56px;
  height: 56px;
  border-radius: var(--border-radius-lg);
  background: var(--color-brand);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  margin-bottom: 16px;
}
.welcome-title {
  font-size: var(--fs-stat, 22px);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
  margin: 0 0 8px;
}
.welcome-sub {
  font-size: var(--fs-body, 14px);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 22px;
}
.welcome-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.wbtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: var(--fs-item);
  font-weight: var(--fw-med);
  border-radius: var(--border-radius-md);
  border: var(--border-width-control) solid var(--color-border-control);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-family: inherit;
}
.wbtn:hover {
  background: var(--color-background-secondary);
}
.wbtn-primary {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}
.wbtn-primary:hover {
  filter: brightness(1.06);
  background: var(--color-brand);
}
.welcome-hint {
  font-size: var(--fs-label);
  color: var(--color-text-tertiary);
  margin-top: 18px;
}

/* ── 联系客服弹窗 ── */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}
.contact-card {
  position: relative;
  width: 360px;
  max-width: 100%;
  background: var(--color-background-primary);
  border-radius: var(--border-radius-lg);
  padding: 28px 24px 22px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
}
.contact-badge {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-md);
  background: var(--color-background-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #07c160;
  margin-bottom: 12px;
}
.contact-title {
  font-size: var(--fs-item);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
  margin: 0 0 8px;
}
.contact-sub {
  font-size: var(--fs-control);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 18px;
}
.wg-qr {
  margin: 0 auto 16px;
}
.qr-box i {
  font-size: 40px;
}
.contact-foot {
  font-size: var(--fs-label);
  color: var(--color-text-tertiary);
  margin: 0;
}
.link {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: var(--color-text-info);
  cursor: pointer;
}
.link:hover {
  text-decoration: underline;
}
</style>
